import { useEffect, useState } from "react";

import { Button, Modal, Table, TableTitleLayout, Tag } from "@shared/ui";

import { PlusOutlined } from "@ant-design/icons";
import { useCreateInfluencer, useGetInfluencers } from "@entities/influencer";
import { useGetInfluencerFacets } from "@entities/influencer/lib/hooks/useGetFacets";
import InfluencerForm from "@entities/influencer/ui/InfluencerForm";
import {
  ICreateInfluencerRequest,
  IInfluencers,
  InfluencerContact,
  InfluencerType,
  SocialMediaEntry,
} from "@shared/lib/types/influencer";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { getSocialMediaUrl } from "@shared/lib/utils/getSocialMediaUrl";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import ColumnFilterWrapper from "@shared/ui/filters/ColumnFilterWrapper/ColumnFilterWrapper";
import { Drawer, Flex, Input, Rate, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import styles from "./styles.module.scss";

import { useBulkUploadInfluencers } from "@entities/influencer/lib/hooks/useBulkUploadInfluencers";
import { InfluencerBulkUploadForm } from "@entities/influencer/ui/InfluencerBulkUploadForm";
import { BriefCreatorColumn } from "@features/breifCreatorColumn/BriefCreatorColumn";
import { SocialMediaInfluencerComp } from "@features/socialMediaInfluencer/SocialMediaInfluencerComp";
import { SelectedFilters } from "@shared/ui/filters/SelectedFilters";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Search } from "lucide-react";

export function InfluencerManagement() {
  const [paegNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedSocialMediaName, setSelectedSocialMediaName] = useState<
    string[]
  >([]);
  const [influencerType, setInfluencerType] = useState<InfluencerType[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [rating, setRating] = useState<number | undefined>(undefined);

  const [selectedInfluencer, setSelectedInfluencer] = useState<string>("");

  const { influencers, isFetching } = useGetInfluencers(
    searchQuery,
    selectedSocialMediaName,
    selectedLocations,
    rating,
    selectedCategory,
    influencerType,
    pageSize,
    paegNo
  );

  const { facets, isFetching: isFetchingInfluencerFacets } =
    useGetInfluencerFacets();

  const { create, isCreating, isCreated } = useCreateInfluencer();

  const [isModalVisible, setModalVisible] = useState(false);

  const [isBulkInfluencerModalVisible, setBulkInfluncerModalVisible] =
    useState(false);

  const {
    update: bulkUploadInfluencers,
    isUpdated,
    isUpdating,
  } = useBulkUploadInfluencers();

  const socialMediasToDisplay =
    (facets?.socialMediaPlatforms || [])?.map((item) => item._id) || [];
  const categoriesToDisplay =
    (facets?.category || [])?.map((item) => item._id) || [];
  const typeToDisplay =
    (facets?.type || [])?.map((item) => item._id as InfluencerType) || [];

  const locationToDisplay =
    (facets?.location || [])?.map((item) => item._id) || [];

  const handleCreateBrand = () => {
    setSelectedInfluencer("");
    setModalVisible(true);
  };

  const handleBulkInfluencerModal = () => {
    setBulkInfluncerModalVisible(true);
  };

  const handleBulkInfluencerModalSubmit = (inputFileUrl: string) => {
    bulkUploadInfluencers({ inputFileUrl });
    setBulkInfluncerModalVisible(false);
  };

  const handleSubmit = (brandData: ICreateInfluencerRequest) => {
    create(brandData);
  };
  const handlePaginationChange = (page: number, size: number) => {
    setPageNo(page);
    setPageSize(size);
  };

  const handleSocialMediaChange = (value: string[] | undefined) => {
    if (!value) return setSelectedSocialMediaName([]);
    setSelectedSocialMediaName(value);
  };

  const handleTypeChange = (value: InfluencerType[] | undefined) => {
    if (!value) return setInfluencerType([]);
    setInfluencerType(value);
  };
  const handleRatingChange = (value: number | undefined) => {
    if (!value) return setRating(undefined);
    setRating(value);
  };
  const handleLocationChange = (value: string[] | undefined) => {
    if (!value) return setSelectedLocations([]);
    setSelectedLocations(value);
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  useEffect(() => {
    setPageNo(1);
  }, [
    searchQuery,
    selectedSocialMediaName,
    selectedLocations,
    rating,
    selectedCategory,
    influencerType,
    pageSize,
  ]);

  const columns: ColumnsType<IInfluencers> = [
    {
      title: "Name",
      dataIndex: "firstName",
      key: "name",
      render: (_: string, record: IInfluencers) => (
        <BriefCreatorColumn
                    influencerId={record.influencerId}
                    influencerName={record.firstName + " " + record.lastName}
                    username={record?.socialMedias?.[0]?.userName}
                    platform={record?.socialMedias?.[0]?.platform}
                    profilePicUrl={record?.profilePicUrl}
                    link={getSocialMediaUrl(
                record?.socialMedias?.[0]?.platform,
                record?.socialMedias?.[0]?.userName
              )}
                    // isCreatorDeclined={record.isCreatorDeclined}
                    // isBrandDeclined={record.isBrandDeclined}
                    // creatorDeclinedReason={record.creatorDeclinedReason}
                    // brandDeclinedReason={record.brandDeclinedReason}
                    showStatusTag={false}
                  />
      ),
    },

    {
      title: "Social Media",
      dataIndex: "socialMedias",
      key: "socialMedias",
      filterCount: selectedSocialMediaName.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Social Media"
          placeholder="Select social media"
          initialTagCount={3}
          itemsToDisplay={socialMediasToDisplay}
          selectedItems={selectedSocialMediaName}
          handleSelectionChange={handleSocialMediaChange}
          props={props}
        />
      ),
      render: (socialMedias: SocialMediaEntry[]) => {
        return (
          <div className={styles.grid}>
            {(socialMedias || [])?.map((item) => {
              const profileUrl = getSocialMediaUrl(
                item.platform,
                item?.socialMedia?.username || item.userName
              );
              return (
                <Space key={item.socialMediaLinkageId}>
                  {getPlatformIcon(item.platform)}{" "}
                  {profileUrl ? (
                    <a
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.userName}
                    </a>
                  ) : (
                    <span>{item.userName}</span>
                  )}
                </Space>
              );
            })}
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filterCount: influencerType.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Type"
          placeholder="Select type"
          initialTagCount={3}
          itemsToDisplay={typeToDisplay}
          selectedItems={influencerType}
          handleSelectionChange={(value: string[]) => {
            handleTypeChange(value as InfluencerType[]);
          }}
          props={props}
        />
      ),
      render: (status: InfluencerType) => <Tag color={"gray"}>{status}</Tag>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      filterCount: selectedCategory.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Category"
          placeholder="Select category"
          initialTagCount={3}
          itemsToDisplay={categoriesToDisplay}
          selectedItems={selectedCategory}
          handleSelectionChange={setSelectedCategory}
          props={props}
        />
      ),
      render: (status: string[]) => (
        <div className={styles.grid}>
          {(status || []).map((item) => (
            <Tag color={"gray"} style={{ width: "max-content" }}>
              {item}
            </Tag>
          ))}
        </div>
      ),
    },

    {
      title: "Contact",
      dataIndex: "contacts",
      key: "contacts",

      render: (contacts: InfluencerContact[]) => {
        return (
          <div className={styles.grid}>
            {(contacts || []).slice(0, 2).map((item) => (
              <>
                {item?.phoneNumber && (
                  <div>
                    {item?.countryCode} {item.phoneNumber}
                  </div>
                )}
                {item?.emailId && <div>{item.emailId}</div>}
              </>
            ))}
            {(contacts || []).length > 2 && <div>...</div>}
          </div>
        );
      },
    },

    {
      title: "Rating",
      dataIndex: "avgReview",
      key: "avgReview",
      width: 200,
      filterCount: rating ? 1 : 0,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => {
        const FilterDropdownContent = () => {
          const [tempRating, setTempRating] = useState<number>(rating || 0);

          return (
            <ColumnFilterWrapper
              title="Rating"
              filterCount={tempRating ? 1 : 0}
              isFiltered={tempRating > 0}
              onReset={() => {
                setTempRating(0);
                handleRatingChange(undefined);
                props.confirm();
              }}
              onApply={() => {
                handleRatingChange(tempRating);
                props.confirm();
              }}
              onClose={props.close}
            >
              <div style={{ padding: "8px 4px", display: "flex", justifyContent: "center" }}>
                <Rate
                  allowHalf
                  value={tempRating}
                  onChange={(value) => setTempRating(value)}
                />
              </div>
            </ColumnFilterWrapper>
          );
        };

        return <FilterDropdownContent />;
      },
      render: (avgReview: number) => (
        <Rate allowHalf disabled value={avgReview} />
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      filterCount: selectedLocations.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Location"
          placeholder="Select location"
          initialTagCount={3}
          itemsToDisplay={locationToDisplay}
          selectedItems={selectedLocations}
          handleSelectionChange={handleLocationChange}
          props={props}
        />
      ),
      render: (location: string[]) => (
        <TextWithTooltip 
          text={location.join(", ")}
          limit={15}
        />
      ),
    },
  ];

  useEffect(() => {
    if (isCreated) {
      setModalVisible(false);
    }
  }, [isCreated]);

  return (
    <div>
      <h1 className={styles.title}>Influencer</h1>

        <Table
          dataSource={influencers?.data}
          columns={columns}
          loading={isFetching || isFetchingInfluencerFacets}
          title={() => <TableTitleLayout 
            filterComponent={
              <div style={{width: "100%"}}>
                <Flex justify="space-between" style={{width: "100%"}}>
          <Input
            placeholder="Search by Name, username, email or phone number"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            prefix={<Search size="14"/>}
            allowClear
            style={{ width: 350 }}
          />
          <Space>
            <Button
              variant="text"
              disabled={isCreating}
              onClick={() => handleBulkInfluencerModal()}
            >
              Bulk Upload Influencers
            </Button>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              disabled={isCreating}
              onClick={() => handleCreateBrand()}
            >
              Create Influencer
            </Button>
          </Space>
          </Flex>
        <SelectedFilters
          filters={{
            location: {
              keyvalue: selectedLocations,
              clearHandler: () => handleLocationChange([]),
            },
            category: {
              keyvalue: selectedCategory,
              clearHandler: () => setSelectedCategory([]),
            },
            type: {
              keyvalue: influencerType,
              clearHandler: () => handleTypeChange([]),
            },
            socialMedias: {
              keyvalue: selectedSocialMediaName,
              clearHandler: () => handleSocialMediaChange([]),
            },
          }}
        />
              </div>
            }
            totalText={`${influencers?.pagination?.totalCount || 0} Records found`}
          />}
          // rowKey="_id"
          pagination={{
            current: paegNo,
            pageSize: pageSize,
            total: influencers?.pagination?.totalCount || 0,
            onChange: handlePaginationChange,
          }}
          scroll={{
            x: "max-content"
          }}
        />
      <Modal
        title={"Create Influencer"}
        open={isModalVisible}
        onCancel={() => {
          setModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <InfluencerForm
          initialCategories={categoriesToDisplay}
          isSubmitting={isCreating}
          onSubmit={handleSubmit}
        />
      </Modal>

      <Modal
        title={"Bulk Upload Influencers"}
        open={isBulkInfluencerModalVisible}
        onCancel={() => {
          setBulkInfluncerModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <InfluencerBulkUploadForm onSubmit={handleBulkInfluencerModalSubmit} />
      </Modal>
      <Drawer
        destroyOnClose
        title={"Influencer"}
        open={Boolean(selectedInfluencer)}
        onClose={() => {
          setSelectedInfluencer("");
        }}
        footer={null}
        width={1300}
      >
        {selectedInfluencer && (
          <SocialMediaInfluencerComp
            creatorInfo={{
              image: influencers?.data?.find(
                (item) => item.influencerId === selectedInfluencer
              )?.profilePicUrl,
              name: influencers?.data?.find(
                (item) => item.influencerId === selectedInfluencer
              )?.firstName,
            }}
            socialMediaInfluencerReq={{
              influencerId: selectedInfluencer,
            }}
          />
        )}
      </Drawer>
    </div>
  );
}
